/**
 * This React provider checks that the user is logged in. It allows child
 * components to call useAdmin() and useTenant() to get information about the
 * logged-in admin and their tenant.
 */
import {
  ApolloError,
  ApolloQueryResult,
  gql,
  ServerError,
  useQuery
} from "@apollo/client";
import { GraphQLError } from "graphql";
import React, { useContext, useEffect } from "react";

import { makeStyles } from "@material-ui/core";

import { SUBACCOUNT_FIELDS, TENANT_FIELDS } from "src/graphql/fragments";
import {
  loggedInInfo_loggedInInfo,
  loggedInInfo,
  loggedInInfo_loggedInInfo_loggedInAdmin,
  loggedInInfo_loggedInInfo_tenant,
  loggedInInfo_loggedInInfo_subaccountMemberships
} from "src/graphql/generated/loggedInInfo";
import { graphqlErrorHasCode } from "src/util/errors";

import { useAuth0 } from "./AuthProvider";
import LoadingIndicator from "./LoadingIndicator";
import LogOutLink from "./LogOutLink";
import DeletedAccount from "./login-errors/DeletedAccount";
import EmailVerification from "./login-errors/EmailVerification";
import ErrorPageLayout from "./login-errors/Layout";
import NoSuchOrganization from "./login-errors/NoSuchOrganization";
import NotAnAdmin from "./login-errors/NotAnAdmin";
import SessionExpired from "./login-errors/SessionExpired";
import StaffTokenMismatch from "./login-errors/StaffTokenMismatch";

export type Tenant = loggedInInfo_loggedInInfo_tenant;
export type LoggedInAdmin = loggedInInfo_loggedInInfo_loggedInAdmin;
export type LoggedInAdminSubaccountMembership =
  loggedInInfo_loggedInInfo_subaccountMemberships;

export const LoggedInAdminContext = React.createContext<{
  loggedInInfo: loggedInInfo_loggedInInfo | null;
  refetch: () => Promise<ApolloQueryResult<loggedInInfo>>;
}>({
  loggedInInfo: null,
  refetch: () =>
    Promise.reject(
      new Error("Cannot refetch; not inside a LoggedInAdminProvider")
    )
});

const GET_LOGGED_IN_ADMIN = gql`
  ${TENANT_FIELDS}
  ${SUBACCOUNT_FIELDS}
  query loggedInInfo {
    loggedInInfo {
      tenant {
        ...TenantFields
      }
      loggedInAdmin {
        id
        fullName
        email
        isStaff
        isTenantOwner
        createdAt
        createdBy {
          id
          fullName
          email
          isStaff
        }
      }
      subaccountMemberships {
        id
        subaccount {
          ...SubaccountFields
        }
        createdAt
        role
      }
    }
  }
`;

const useStyles = makeStyles(() => ({
  pageWrapper: {
    flex: "1"
  }
}));

function ErrorPage({ error }: { error: ApolloError }): JSX.Element {
  const serverError = error.networkError as ServerError | undefined;
  if (serverError?.statusCode === 401) {
    return <NoSuchOrganization />;
  }

  const errorMessages: undefined | readonly GraphQLError[] =
    serverError?.result?.errors;

  if (serverError?.message.toLowerCase().includes("invalid refresh token")) {
    return <SessionExpired />;
  }

  if (errorMessages && errorMessages.length) {
    if (graphqlErrorHasCode("UNVERIFIED_EMAIL", errorMessages)) {
      return <EmailVerification />;
    }
    if (graphqlErrorHasCode("NOT_AN_ADMIN", errorMessages)) {
      return <NotAnAdmin />;
    }
    if (graphqlErrorHasCode("ADMIN_DELETED", errorMessages)) {
      return <DeletedAccount />;
    }
    if (graphqlErrorHasCode("AUTH0_TOKEN_STAFF_MISMATCH", errorMessages)) {
      return <StaffTokenMismatch />;
    }
  }

  let title: string;
  let message: React.ReactNode;
  if (serverError?.statusCode && serverError?.statusCode >= 500) {
    title = "Server error";
    message =
      "We encountered an error processing your request. Please try again later.";
  } else if (serverError?.statusCode && serverError?.statusCode === 410) {
    title = "Dialer Organization Deactivated";
    message = (
      <>
        This account has been deactivated. If you’d like to start calling again,
        please fill out the
        <a href="https://www.scaletowin.com/get-started">Get Started form</a>.
      </>
    );
  } else if (serverError?.statusCode && serverError?.statusCode >= 400) {
    title = "Request error";
    message =
      "We were unable to load your user account data due to an invalid request. Please try again later.";
  } else if (serverError?.message === "Failed to fetch") {
    title = "Network error";
    message =
      "We were unable to load your user account data due to a network error. Please check your internet connection and try again.";
  } else {
    title = "Unknown error";
    message =
      "Error loading Dialer data. Please check your internet connection and try again.";
  }

  return (
    <ErrorPageLayout title={title}>
      <p>{message}</p>
      <LogOutLink />
    </ErrorPageLayout>
  );
}

export function useAdmin(): LoggedInAdmin | null {
  return useContext(LoggedInAdminContext).loggedInInfo?.loggedInAdmin ?? null;
}

export function useTenant(): Tenant | null {
  return useContext(LoggedInAdminContext).loggedInInfo?.tenant ?? null;
}

export function useAdminSubaccountMemberships():
  | LoggedInAdminSubaccountMembership[]
  | null {
  return (
    useContext(LoggedInAdminContext).loggedInInfo?.subaccountMemberships ?? null
  );
}

export function useLoginInfoRefetch(): () => Promise<
  ApolloQueryResult<loggedInInfo>
> {
  return useContext(LoggedInAdminContext).refetch;
}

export default function LoggedInAdminProvider({
  children
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  const { loading, error, data, refetch } = useQuery<loggedInInfo>(
    GET_LOGGED_IN_ADMIN,
    {
      pollInterval: 30 * 1000
    }
  );

  // we save `data` so that if a poll iteration fails, we can keep using the
  // old data
  const [dataCache, setDataCache] = React.useState<loggedInInfo | null>(null);
  useEffect(() => {
    if (data && !error) {
      setDataCache(data);
    }
  }, [data, error]);

  const auth0 = useAuth0();
  const classes = useStyles();

  async function refetchLogInInfo() {
    // First, force-refresh the auth0 token in case any auto0 data has been
    // updated (e.g. the email was verified)
    await auth0.getAccessTokenSilently({
      cacheMode: "off"
    });

    // then, re-fetch info from the server
    return refetch();
  }

  if (dataCache) {
    return (
      <LoggedInAdminContext.Provider
        value={{
          loggedInInfo: dataCache.loggedInInfo,
          refetch: refetchLogInInfo
        }}
      >
        {children}
      </LoggedInAdminContext.Provider>
    );
  }

  if (error) {
    return (
      <LoggedInAdminContext.Provider
        value={{
          loggedInInfo: null,
          refetch: refetchLogInInfo
        }}
      >
        <ErrorPage error={error} />
      </LoggedInAdminContext.Provider>
    );
  }

  if (loading) {
    return (
      <div className={classes.pageWrapper}>
        <LoadingIndicator />
      </div>
    );
  }

  return null;
}
